import React, { useState, useEffect } from 'react';
import { Users, Search, ChevronRight, PlusCircle } from 'lucide-react';
import supabase from '../../supabase';
import EmployeeForm from './EmployeeForm';
import './EmployeeDashboard.css';

const EmployeeDashboard = ({ onEmployeeSelect, modalState, openModal, closeModal }) => {
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError('');

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('User not authenticated');

      const { data, error: fetchError } = await supabase
        .from('employees')
        .select('*')
        .eq('user_id', user.id)
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;

      setEmployees(data || []);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError(err.message || 'Failed to load employees');
      setEmployees([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => fetchEmployees(true);

  const filteredEmployees = employees.filter(emp =>
    emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.emp_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEmployeeSelect = (employee) => {
    if (onEmployeeSelect) {
      onEmployeeSelect(employee.id, employee.name);
    }
  };

  const handleNewTeamMember = () => {
    if (openModal) {
      openModal('add');
    }
  };

  const handleFormClose = () => {
    if (closeModal) {
      closeModal();
    }
    fetchEmployees(); // Refresh list after adding
  };

  const generateFallbackAvatar = (name) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Unknown')}&size=64&background=4f46e5&color=ffffff&bold=true`;
  };

  // Show add employee form when modalState action is 'add'
  if (modalState?.action === 'add') {
    return <EmployeeForm onBack={handleFormClose} />;
  }

  if (loading) {
    return (
      <div className="employeedashboard-container">
        <div className="employeedashboard-loading">
          <div className="employeedashboard-spinner"></div>
          <p>Loading employees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="employeedashboard-container">
      <div className="employeedashboard-wrapper">
        <div className="employeedashboard-header">
          <div className="employeedashboard-header-content">
            <h1 className="employeedashboard-title">
              Team
              <div className="employeedashboard-title-count">
                <Users size={20} />
                <span>{employees.length}</span>
              </div>
            </h1>
          </div>
          <button 
            className="employeedashboard-new-member-btn"
            onClick={handleNewTeamMember}
            title="Add New Team Member"
          >
            <span>Add Team Member</span>
            <PlusCircle size={18} className="add-icon" />
          </button>
        </div>

        {error && (
          <div className="employeedashboard-error">
            <p>{error}</p>
            <button onClick={() => fetchEmployees()} className="employeedashboard-retry-button">
              Try Again
            </button>
          </div>
        )}

        {!error && (
          <>
            <div className="employeedashboard-search-wrapper">
              <Search className="employeedashboard-search-icon" size={20} />
              <input
                type="text"
                placeholder="Search employees by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="employeedashboard-search-input"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="employeedashboard-search-clear"
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>

            {filteredEmployees.length === 0 ? (
              <div className="employeedashboard-empty">
                <Users size={64} style={{ opacity: 0.5, marginBottom: '1rem' }} />
                <p>
                  {employees.length === 0 
                    ? 'No employees added yet. Add your first employee to get started!' 
                    : `No employees match "${searchTerm}"`}
                </p>
                {employees.length === 0 && (
                  <button 
                    className="employeedashboard-empty-cta-btn"
                    onClick={handleNewTeamMember}
                  >
                    Add Your First Team Member
                  </button>
                )}
              </div>
            ) : (
              <div className="employeedashboard-grid">
                {filteredEmployees.map((employee) => (
                  <div
                    key={employee.id}
                    className="employeedashboard-card"
                    onClick={() => handleEmployeeSelect(employee)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleEmployeeSelect(employee);
                    }}
                  >
                    <div className="employeedashboard-card-content">
                      <div className="employeedashboard-card-header">
                        <img
                          src={employee.image || generateFallbackAvatar(employee.name)}
                          alt={`${employee.name}'s profile`}
                          className="employeedashboard-card-avatar"
                          onError={(e) => { e.target.src = generateFallbackAvatar(employee.name); }}
                          loading="lazy"
                        />
                        <div className="employeedashboard-card-info">
                          <h3 className="employeedashboard-card-name" title={employee.name}>
                            {employee.name}
                          </h3>
                          <p className="employeedashboard-card-id" title={employee.emp_id}>
                            ID: {employee.emp_id || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="employeedashboard-card-footer">
                        <span className="employeedashboard-card-view-details">
                          View Details
                          <ChevronRight size={16} />
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeDashboard;